import type { ReactNode } from "react";
import { RotateCw } from "lucide-react";
import {
  KioskLoadingPanel,
  KioskStatePanel,
} from "../../../components/kiosk-state-panel";

/**
 * Centres a content-sized state panel in the step area without stretching it.
 */
function StepStatusFrame({ children }: { readonly children: ReactNode }) {
  return (
    <div className="flex min-h-full w-full min-w-0 flex-col justify-center px-2 py-6">
      {children}
    </div>
  );
}

/**
 * Centered kiosk loading state shared by self-service steps. Delegates to
 * {@link KioskLoadingPanel}; new steps should use that panel inside a
 * `KioskStep align="center"` directly.
 *
 * @since 1.1.8
 * @example
 * ```tsx
 * <StepLoading message={t("common.loading")} />
 * ```
 */
export function StepLoading({ message }: { readonly message: ReactNode }) {
  return (
    <StepStatusFrame>
      <KioskLoadingPanel title={message} />
    </StepStatusFrame>
  );
}

/**
 * Centered kiosk failure state with an icon and an explicit retry action.
 * Delegates to the shared {@link KioskStatePanel} (`tone="error"`), so every
 * failure looks the same; new steps should use that panel directly and give
 * the error a heading.
 *
 * @since 1.1.8
 * @example
 * ```tsx
 * <StepError icon={<AlertCircle />} message={error} retryTestId="x-retry" retryLabel={t("common.retry")} onRetry={retry} />
 * ```
 */
export function StepError({ icon, message, messageTestId, retryTestId, onRetry, retryLabel }: {
  readonly icon: ReactNode;
  readonly message: ReactNode;
  readonly messageTestId?: string;
  readonly retryTestId: string;
  readonly retryLabel: string;
  readonly onRetry: () => void;
}) {
  return (
    <StepStatusFrame>
      <KioskStatePanel
        tone="error"
        icon={icon}
        title={message}
        titleTestId={messageTestId}
        action={{ label: retryLabel, icon: RotateCw, onClick: onRetry, testId: retryTestId }}
      />
    </StepStatusFrame>
  );
}
